import React, { type FC, memo, use, useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Button, Modal } from "@heroui/react";
import { Sparkles } from "@gravity-ui/icons";

import { AgentMessageView } from "./AgentMessageView";
import { AGENT_STEPS } from "./steps";
import type { AgentContext, AgentStep } from "./runner/types";
import { useAgentContext } from "./useAgentContext";
import { useAgentRun } from "./useAgentRun";
import { CompanyContext } from "@domain/context/CompanyContext";
import { useTranslation } from "@domain/intl/useTranslation";
import { ProductColors } from "@ui-components/ProductColors";

interface ModalProps {
    isOpen: boolean;
    onClose(): void;
    /** Defaults to the full scenario; tests pass their own. */
    steps?: AgentStep[];
    /** Defaults to `useAgentContext()`; tests pass a fake. */
    context?: AgentContext;
}

interface ChoiceProps {
    value: string;
    primary?: boolean;
    onAnswer(value: string): void;
    children: React.ReactNode;
}

const ChoiceButton: FC<ChoiceProps> = ({ value, primary, onAnswer, children }) => {
    const handlePress = useCallback(() => onAnswer(value), [onAnswer, value]);
    return (
        <Button variant={primary ? "primary" : "secondary"} className="rounded-full" onPress={handlePress}>
            {children}
        </Button>
    );
};

interface ChatProps {
    steps: AgentStep[];
    context: AgentContext;
    onClose(): void;
}

const AgentChat: FC<ChatProps> = ({ steps, context, onClose }) => {
    const { t } = useTranslation();
    const { companyGQL } = use(CompanyContext);
    const run = useAgentRun(steps, context);
    const { start, state, cancel } = run;
    const [confirmStop, setConfirmStop] = useState(false);
    const logRef = useRef<HTMLDivElement>(null);

    useEffect(() => start(), [start]);
    useEffect(() => {
        const log = logRef.current;
        if (log) log.scrollTop = log.scrollHeight;
    }, [run.messages]);

    // While the finAPI form is open the run waits for the bank, so pausing makes no sense; the user may skip the bank
    const bankImportMessageId = run.messages.find((m) => m.panel?.type === "bankImport")?.id;
    const { completePanel } = run;
    const handleSkipBank = useCallback(() => {
        if (bankImportMessageId) completePanel(bankImportMessageId, "skipped");
    }, [completePanel, bankImportMessageId]);

    const isActive = state === "running" || state === "paused";
    const handleClose = useCallback(() => (isActive ? setConfirmStop(true) : onClose()), [isActive, onClose]);
    const handleConfirmStop = useCallback(() => {
        cancel();
        onClose();
    }, [cancel, onClose]);
    const handleKeep = useCallback(() => setConfirmStop(false), []);
    const handleOpenChange = useCallback((open: boolean) => !open && handleClose(), [handleClose]);

    const progressLabel =
        state === "finished"
            ? t("app.agent.finished")
            : run.stepNumber > 0
              ? t("app.agent.stepOf", { step: run.stepNumber, total: run.stepTotal })
              : "";

    const subtitle = [companyGQL?.name, String(context.year), progressLabel].filter(Boolean).join(" · ");

    return (
        <Modal.Backdrop isOpen onOpenChange={handleOpenChange}>
            <Modal.Container>
                <Modal.Dialog className="w-140 max-w-full overflow-hidden p-0" data-testid="binale-agent-modal">
                    <Modal.CloseTrigger onPress={handleClose} />
                    <Modal.Header className={`flex items-center gap-2 px-4 py-3 ${ProductColors.AIGradient.className}`}>
                        <Sparkles aria-hidden className="size-4" />
                        <Modal.Heading className="text-white">{t("app.agent.title")}</Modal.Heading>
                        <small data-testid="agent-header-info" className="ml-auto mr-8 opacity-90">
                            {subtitle}
                        </small>
                    </Modal.Header>
                    <Modal.Body className="flex h-96 flex-col gap-2 overflow-y-auto bg-default/40 p-3" ref={logRef}>
                        <AnimatePresence initial={false}>
                            {run.messages.map((message) => (
                                <AgentMessageView
                                    key={message.id}
                                    message={message}
                                    onPanelDone={run.completePanel}
                                    onClose={onClose}
                                />
                            ))}
                        </AnimatePresence>
                    </Modal.Body>
                    <Modal.Footer className="flex flex-wrap justify-start gap-2 border-t border-separator p-3">
                        {confirmStop ? (
                            <div
                                role="alertdialog"
                                aria-label={t("app.agent.confirmStop.title")}
                                className="flex w-full flex-col gap-2"
                            >
                                <strong>{t("app.agent.confirmStop.title")}</strong>
                                <span className="text-sm text-muted">{t("app.agent.confirmStop.body")}</span>
                                <div className="flex gap-2">
                                    <Button variant="danger" onPress={handleConfirmStop}>
                                        {t("app.agent.stop")}
                                    </Button>
                                    <Button variant="outline" onPress={handleKeep}>
                                        {t("app.agent.confirmStop.keep")}
                                    </Button>
                                </div>
                            </div>
                        ) : run.pending ? (
                            run.pending.choices.map((choice) => (
                                <ChoiceButton
                                    key={choice.value}
                                    value={choice.value}
                                    primary={choice.primary}
                                    onAnswer={run.answer}
                                >
                                    {t(choice.labelId)}
                                </ChoiceButton>
                            ))
                        ) : isActive ? (
                            <>
                                {bankImportMessageId ? (
                                    <Button variant="secondary" onPress={handleSkipBank}>
                                        {t("app.agent.bankImport.skipBank")}
                                    </Button>
                                ) : state === "paused" ? (
                                    <Button variant="primary" onPress={run.resume}>
                                        {t("app.agent.resume")}
                                    </Button>
                                ) : (
                                    <Button variant="secondary" onPress={run.pause}>
                                        {t("app.agent.pause")}
                                    </Button>
                                )}
                                <Button variant="outline" onPress={handleClose}>
                                    {t("app.agent.stop")}
                                </Button>
                            </>
                        ) : (
                            <Button variant="primary" onPress={onClose}>
                                {t("app.agent.close")}
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal.Dialog>
            </Modal.Container>
        </Modal.Backdrop>
    );
};

const AgentChatWithContext: FC<Omit<ChatProps, "context">> = (props) => {
    const context = useAgentContext();
    return <AgentChat {...props} context={context} />;
};

const BinaleAgentModalImpl: FC<ModalProps> = ({ isOpen, onClose, steps = AGENT_STEPS, context }) => {
    // Content mounts per open, so each opening is a fresh run and a fresh context.
    if (!isOpen) return null;
    return context ? (
        <AgentChat steps={steps} context={context} onClose={onClose} />
    ) : (
        <AgentChatWithContext steps={steps} onClose={onClose} />
    );
};

export const BinaleAgentModal = memo(BinaleAgentModalImpl);
